import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Button, NewModal, Spinner } from '@factorialco/deprecated-design-system'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { MeetingData } from 'modules/onboarding/components/HelpBox/HubspotModal/types'
import { MeetingPayload } from 'modules/onboarding/lib/hubspot/meetings'
import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'
import useMeetingData from 'modules/upsell/hooks/useMeetingData'
import { RequestUpgradeProps } from 'modules/upsell/lib/requestUpgradeTypes'

import ThankYouModal from '../ThankYouModal'
import { UpsellModaContextProvider, useUpsellModalContext } from './_Context'
import MeetingBookingModal from './_MeetingBookingModal'
import RequestInfoButton from './_RequestInfoButton'
import ThankYouTrialModal from './_ThankYouTrialModal'

type Step =
  | 'upsell-step'
  | 'thankyou-request-info-step'
  | 'thankyou-trial-step'
  | 'book-a-meeting-step'

interface UpsellModalProps {
  requestDetail: RequestUpgradeProps
  withTrialCta?: boolean
  onClose: () => void
  bookingMeeting?: boolean
}
interface ImageModalProps extends UpsellModalProps {
  buttonLabel: string
  stepsContent: Omit<React.ComponentProps<typeof NewModal.ImageStep>, 'children'>[]
  onLoad?: () => void
}
interface TextModalProps extends UpsellModalProps {
  buttonLabel: string
  itemsContent: Omit<React.ComponentProps<typeof NewModal.TextStep>, 'children'>
  onLoad?: () => void
}
interface BaseModalProps extends UpsellModalProps {
  step: Step
  onLoad?: () => void
  children: React.ComponentProps<typeof NewModal>['children']
  onMeetingBooked?: (data: MeetingPayload) => void
  meetingData?: Partial<MeetingData>
}

type ButtonsProps = {
  buttonLabel: string
  bookingMeeting: boolean
  shouldShowCalendar: () => Promise<boolean>
}

const Buttons = ({ buttonLabel, bookingMeeting, shouldShowCalendar }: ButtonsProps) => {
  const { setStep, requestDetail, shouldShowTrialCta, loading } = useUpsellModalContext()
  const [showBookingButton, setShowBookingButton] = React.useState(false)

  useUnsafeEffect(() => {
    shouldShowCalendar().then((shouldShow) => {
      setShowBookingButton(shouldShow)
    })
  }, [shouldShowCalendar, showBookingButton])

  const eventProps = {
    module_requested: requestDetail.moduleRequested,
    request_reason: requestDetail.requestReason,
    request_source: requestDetail.requestSource,
  }

  return (
    <Box flexDirection="row" gap="s12" justifyContent="center">
      {loading ? (
        <Spinner />
      ) : (
        <>
          {shouldShowTrialCta && (
            <Button
              type="secondary"
              onClick={() => {
                track(trackingEvents.upsell.upsellTrialButtonClicked, eventProps)
                setStep('thankyou-trial-step')
              }}
            >
              {i18n.t('marketplace.modules.trial_upsell.button')}
            </Button>
          )}
          <RequestInfoButton
            type="upgrade"
            requestDetail={requestDetail}
            buttonLabel={buttonLabel}
            onClick={() => {
              track(trackingEvents.upsell.upsellDemoButtonClicked, eventProps)
              setStep('thankyou-request-info-step')
            }}
          />
          {bookingMeeting && showBookingButton && (
            <Button
              type="secondary"
              onClick={() => {
                track(trackingEvents.upsell.openUpsellBookingCalendar)
                setStep('book-a-meeting-step')
              }}
            >
              {i18n.t('upsell.book_a_meeting')}
            </Button>
          )}
        </>
      )}
    </Box>
  )
}

const BaseModal = ({ onClose, children, onMeetingBooked, onLoad, meetingData }: BaseModalProps) => {
  const { pathname } = useLocation()
  const { currentStep, requestDetail } = useUpsellModalContext()
  const { create } = useCreateUpsellRequest()

  const upsellOnClick = async () => {
    await create({ ...requestDetail, meetingBooked: true })
  }

  useEffect(() => {
    onLoad && onLoad()
  }, [])

  useEffect(() => {
    track(trackingEvents.upsell.openModalWithUpsellInfo, {
      feature_info: requestDetail.moduleRequested,
      page_path: pathname,
    })

    if (onMeetingBooked && meetingData?.meetingBookSucceeded) {
      upsellOnClick()
    }
  }, [requestDetail.moduleRequested])

  return (
    <>
      {currentStep === 'upsell-step' && (
        <NewModal onClose={onClose} onFinish={onClose} notCloseable>
          {children}
        </NewModal>
      )}
      {currentStep === 'thankyou-request-info-step' && <ThankYouModal onClose={onClose} />}
      {currentStep === 'thankyou-trial-step' && <ThankYouTrialModal onClose={onClose} />}
      {currentStep === 'book-a-meeting-step' && (
        <MeetingBookingModal
          onClose={onClose}
          onMeetingBooked={() => onMeetingBooked && onMeetingBooked}
        />
      )}
    </>
  )
}

const Text = ({
  requestDetail,
  buttonLabel,
  onClose,
  itemsContent,
  withTrialCta = false,
  bookingMeeting = false,
}: TextModalProps) => {
  const [step, setStep] = React.useState<Step>('upsell-step')
  const { onMeetingBooked, shouldShowCalendar, meetingData } = useMeetingData({
    onSuccessEvent: trackingEvents.upsell.upsellMeetingBooked,
  })

  return (
    <UpsellModaContextProvider
      currentStep={step}
      setStep={setStep}
      requestDetail={requestDetail}
      withTrialCta={withTrialCta}
    >
      <BaseModal
        step={step}
        onClose={onClose}
        requestDetail={requestDetail}
        onMeetingBooked={onMeetingBooked}
        meetingData={meetingData}
      >
        <NewModal.TextStep {...itemsContent}>
          <Buttons
            buttonLabel={buttonLabel}
            bookingMeeting={bookingMeeting}
            shouldShowCalendar={shouldShowCalendar}
          />
        </NewModal.TextStep>
      </BaseModal>
    </UpsellModaContextProvider>
  )
}

const Image = ({
  requestDetail,
  stepsContent,
  buttonLabel,
  onClose,
  withTrialCta = false,
  bookingMeeting = false,
}: ImageModalProps) => {
  const [step, setStep] = React.useState<Step>('upsell-step')
  const { onMeetingBooked, shouldShowCalendar } = useMeetingData({
    onSuccessEvent: trackingEvents.upsell.upsellMeetingBooked,
  })
  return (
    <UpsellModaContextProvider
      currentStep={step}
      setStep={setStep}
      requestDetail={requestDetail}
      withTrialCta={withTrialCta}
    >
      <BaseModal
        step={step}
        onClose={onClose}
        requestDetail={requestDetail}
        onMeetingBooked={onMeetingBooked}
      >
        {stepsContent.map((content) => (
          <NewModal.ImageStep
            key={content.title}
            src={content.src}
            title={content.title}
            description={content.description}
          >
            <Buttons
              buttonLabel={buttonLabel}
              bookingMeeting={bookingMeeting}
              shouldShowCalendar={shouldShowCalendar}
            />
          </NewModal.ImageStep>
        ))}
      </BaseModal>
    </UpsellModaContextProvider>
  )
}

export default { Image, Text }
